import { trackEvent } from "@/lib/analytics";
import { capturePostHogEvent } from "@/lib/analytics-posthog";
import { readBrowserAttribution } from "@/components/analytics/browser-attribution";

export type PublishingSurface = "comparison" | "medium_builder";

export type PublishingEventName =
  | "publishing_cta_clicked"
  | "publishing_faq_expanded"
  | "publishing_section_viewed";

type PublishingPropertyValue = string | number | boolean | null | undefined;

export type PublishingEventProperties = Record<
  string,
  PublishingPropertyValue
> & {
  surface: PublishingSurface;
};

function referrerHost(referrer: string): string | null {
  if (!referrer) return null;

  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function isOrganicSearchHost(host: string | null): boolean {
  if (!host) return false;

  return (
    host === "bing.com" ||
    host.endsWith(".bing.com") ||
    host === "duckduckgo.com" ||
    host.endsWith(".duckduckgo.com") ||
    host === "ecosia.org" ||
    host.endsWith(".ecosia.org") ||
    host === "search.brave.com" ||
    host.startsWith("google.") ||
    host.includes(".google.") ||
    host === "search.yahoo.com" ||
    host.endsWith(".search.yahoo.com")
  );
}

function deviceCategory(width: number): "mobile" | "tablet" | "desktop" {
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function pathnameFromAttribution(value: string | undefined): string | null {
  if (!value) return null;

  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return null;
  }
}

/**
 * Shared, privacy-conscious context for public publishing events. Referrers are
 * reduced to the host so query strings and path data never enter analytics.
 */
export function getPublishingEventContext(): Record<
  string,
  PublishingPropertyValue
> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const storedAttribution = readBrowserAttribution();
  const host = referrerHost(storedAttribution?.referrer ?? document.referrer);
  const landingPage =
    pathnameFromAttribution(storedAttribution?.landingPage) ??
    window.location.pathname;

  return {
    page_path: window.location.pathname,
    referrer: host,
    campaign: storedAttribution?.utmCampaign ?? params.get("utm_campaign"),
    device: deviceCategory(window.innerWidth),
    organic_search_landing_page: isOrganicSearchHost(host)
      ? landingPage
      : null,
  };
}

/** Send the same publishing event to GA4 and PostHog through their consent gates. */
export function capturePublishingEvent(
  name: PublishingEventName,
  properties: PublishingEventProperties,
): void {
  const payload = {
    ...getPublishingEventContext(),
    ...properties,
  };

  trackEvent(name, payload);
  capturePostHogEvent(name, payload);
}
